import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, GitBranch, AlertTriangle } from 'lucide-react';
import {
  listRelationships,
  createRelationship,
  deleteRelationship,
} from '@/api/relationships';
import type { Relationship } from '@/api/relationships';
import { queryKeys } from '@/api/queryKeys';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { EntitlementGate } from '@/components/billing/EntitlementGate';

/** Truncate a UUID or long ID for display. */
function truncateId(id: string): string {
  if (id.length <= 13) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
}

/** Format an ISO 8601 date as "Apr 29, 2026". */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// "Add Relationship" dialog
// ---------------------------------------------------------------------------
interface AddDialogProps {
  open: boolean;
  onClose: () => void;
}

function AddRelationshipDialog({ open, onClose }: AddDialogProps) {
  const queryClient = useQueryClient();
  const [subjectPrincipalId, setSubjectPrincipalId] = useState('');
  const [relationName, setRelationName] = useState('');
  const [objectResourceId, setObjectResourceId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createRelationship,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.relationships.list });
      handleClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create relationship');
    },
  });

  function handleClose() {
    setSubjectPrincipalId('');
    setRelationName('');
    setObjectResourceId('');
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subjectPrincipalId.trim() || !relationName.trim() || !objectResourceId.trim()) return;
    setError(null);
    createMutation.mutate({
      subject_principal_id: subjectPrincipalId.trim(),
      relation_name: relationName.trim(),
      object_resource_id: objectResourceId.trim(),
    });
  }

  const isValid =
    subjectPrincipalId.trim() !== '' &&
    relationName.trim() !== '' &&
    objectResourceId.trim() !== '';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Relationship</DialogTitle>
          <DialogDescription>
            Create a direct REBAC relationship between a principal and a resource.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="subject-principal-id">
              Subject Principal ID <span className="text-destructive">*</span>
            </label>
            <Input
              id="subject-principal-id"
              placeholder="UUID of the principal"
              value={subjectPrincipalId}
              onChange={(e) => setSubjectPrincipalId(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="relation-name">
              Relation Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="relation-name"
              placeholder="e.g. viewer, editor, owner"
              value={relationName}
              onChange={(e) => setRelationName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="object-resource-id">
              Object Resource ID <span className="text-destructive">*</span>
            </label>
            <Input
              id="object-resource-id"
              placeholder="UUID of the resource"
              value={objectResourceId}
              onChange={(e) => setObjectResourceId(e.target.value)}
              required
            />
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending ? 'Adding...' : 'Add Relationship'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// "Delete relationship" confirmation dialog
// ---------------------------------------------------------------------------
interface DeleteDialogProps {
  relationship: Relationship | null;
  onClose: () => void;
}

function DeleteRelationshipDialog({ relationship, onClose }: DeleteDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRelationship(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.relationships.list });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to delete relationship');
    },
  });

  if (!relationship) return null;

  return (
    <Dialog open={!!relationship} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Relationship</DialogTitle>
          <DialogDescription>
            Remove the{' '}
            <span className="font-medium text-foreground">{relationship.relation_name}</span>{' '}
            relationship between principal{' '}
            <code className="font-mono">{truncateId(relationship.subject_principal_id)}</code>{' '}
            and resource{' '}
            <code className="font-mono">{truncateId(relationship.object_resource_id)}</code>.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(relationship.id)}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export function RelationshipsPage() {
  const [addOpen, setAddOpen] = useState(false);
  const [relationshipToDelete, setRelationshipToDelete] = useState<Relationship | null>(null);

  const { data: relationshipsResponse, isLoading, isError } = useQuery({
    queryKey: queryKeys.relationships.list,
    queryFn: () => listRelationships(),
  });

  const items: Relationship[] = relationshipsResponse?.data ?? [];

  return (
    <EntitlementGate feature="rebac_policies">
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          eyebrow="Govern"
          title="Relationships"
          subtitle="Manage direct REBAC relationships between principals and resources."
          actions={
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Relationship
            </Button>
          }
        />

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Relationships</CardTitle>
            <CardDescription>
              Direct access relationships grant a principal a named relation on a specific resource,
              evaluated by the REBAC policy engine.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-3 px-6 pb-6">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex items-center gap-2 px-6 pb-6 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4" />
                Failed to load relationships. Please refresh.
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
                <GitBranch className="h-8 w-8 opacity-40" />
                <p className="text-sm">No relationships yet.</p>
                <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add your first relationship
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Principal ID
                      </th>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Relation
                      </th>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Resource ID
                      </th>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((rel) => (
                      <tr key={rel.id} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-6 py-3">
                          <code
                            className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono"
                            title={rel.subject_principal_id}
                          >
                            {truncateId(rel.subject_principal_id)}
                          </code>
                        </td>
                        <td className="px-6 py-3 font-medium">{rel.relation_name}</td>
                        <td className="px-6 py-3">
                          <code
                            className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono"
                            title={rel.object_resource_id}
                          >
                            {truncateId(rel.object_resource_id)}
                          </code>
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">
                          {formatDate(rel.created_at)}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRelationshipToDelete(rel)}
                            aria-label={`Delete relationship ${rel.id}`}
                            title="Delete relationship"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialogs */}
        <AddRelationshipDialog open={addOpen} onClose={() => setAddOpen(false)} />
        <DeleteRelationshipDialog
          relationship={relationshipToDelete}
          onClose={() => setRelationshipToDelete(null)}
        />
      </div>
    </EntitlementGate>
  );
}
